import { Button, Col, Row } from "reactstrap";
import { useEffect, useRef, useState, useCallback } from "react";
import "react-circular-progressbar/dist/styles.css";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import RotatePhonePopup from "../RotatePhonePopup";
import NoInternet from '../NoInternet';
import NoDataFound2 from '../NoData2';
import Lottie from 'lottie-react';
import { useSnackbar } from "../context/SnackbarContext";
import { useLanguage } from "../context/Languagecontext";
import { useTranslatedText } from "../../hooks/useTranslatedText";

import spinWin from "../../audio/spinwin.wav";

import veryhighLottie from '../../lottie/angry.json';
import highLottie from '../../lottie/angry.json';
import goodLottie from '../../lottie/love.json';
import averageLottie from '../../lottie/happy.json';
import lowLottie from '../../lottie/sad.json';

// img
import reputationbutton from "../../img/que12/reputationbutton.png"
import tap from "../../img/que12/tap.svg"
import star from "../../img/star2.png"

import NoNetDark from "../../img/NoNetDark.png"
import NouserDark from "../../img/NoUserFoundDark.png";

// ── Tier config ──────────────────────────────────────────────────
const TIERS = [
    { key: "low", circlePoints: 14, linePoints: 6, totalPoints: 20, color: "#773EF4" },
    { key: "average", circlePoints: 14, linePoints: 6, totalPoints: 20, color: "#9A50CA" },
    { key: "good", circlePoints: 14, linePoints: 6, totalPoints: 20, color: "#C1659C" },
    { key: "high", circlePoints: 14, linePoints: 6, totalPoints: 20, color: "#DA737E" },
    { key: "veryhigh", circlePoints: 20, linePoints: 0, totalPoints: 20, color: "#EB7C69" },
];

const getLottieColor = (key) => {
    const tier = TIERS.find(t => t.key === key);
    return tier ? tier.color : "#ffffff";
};

const TOTAL_POINTS = 100;

const getFillFractions = (points) => {
    const clamped = Math.max(0, Math.min(TOTAL_POINTS, points));
    return TIERS.map((tier, i) => {
        const tierStart = TIERS.slice(0, i).reduce((s, t) => s + t.totalPoints, 0);
        const tierPoints = Math.max(0, Math.min(clamped - tierStart, tier.totalPoints));
        if (tierPoints <= 0) return { circleFill: 0, lineFill: 0 };
        const circleFill = Math.min(tierPoints, tier.circlePoints) / tier.circlePoints;
        const lineRemaining = Math.max(0, tierPoints - tier.circlePoints);
        const lineFill = tier.linePoints > 0 ? lineRemaining / tier.linePoints : 0;
        return { circleFill, lineFill };
    });
};

const getLottieForPoints = (points) => {
    if (points <= 0) return null;
    if (points <= 20) return "low";
    if (points <= 40) return "average";
    if (points <= 60) return "good";
    if (points <= 80) return "high";
    return "veryhigh";
};

// ── storeImageInLocalStorage kept for starImage only ────────────
const storeImageInLocalStorage = (key, url) => {
    const stored = localStorage.getItem(key);
    if (stored) return Promise.resolve(stored);
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = url;
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL('image/png');
            try { localStorage.setItem(key, dataURL); } catch (e) {
                console.warn(`localStorage quota exceeded for key "${key}", using in-memory fallback.`);
            }
            resolve(dataURL);
        };
        img.onerror = reject;
    });
};


const Category12 = ({ hint, uploadedImage, originalData, username, category }) => {
    const { lanText, t } = useLanguage();
    const [activeLottie, setActiveLottie] = useState(null);
    const navigate = useNavigate();
    const [noInternet, setNoInternet] = useState(false);

    // ── Press-fill animation state ───────────────────────────────
    const [fillPoints, setFillPoints] = useState(0);
    const fillPointsRef = useRef(0);
    const pressIntervalRef = useRef(null);
    const isPressedRef = useRef(false);

    // ── noNet/noUser images ──────────────────────────────────────
    const [noNetDarkImgSrc, setNoNetDarkImgSrc] = useState(null);
    const [noNetImgLoaded, setNoNetImgLoaded] = useState(false);
    const [noUserDarkImgSrc, setNoUserDarkImgSrc] = useState(null);
    const [noUserImgLoaded, setNoUserImgLoaded] = useState(false);

    const [imagesLoaded, setImagesLoaded] = useState(false);
    const [imageLoadError, setImageLoadError] = useState(false);

    const [starImgSrc, setStarImgSrc] = useState(null);
    const [starImgLoaded, setStarImgLoaded] = useState(false);

    const navigationTimerRef = useRef(null);
    const spinWinAudioRef = useRef(null);

    const { showSnackbar } = useSnackbar();

    const preloadImages = [reputationbutton, star];

    preloadImages.forEach((src) => {
        const img = new Image();
        img.src = src;
    });

    const isSmallScreen = window.innerWidth < 380 && window.innerWidth !== 360;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    const data = originalData.question;

    const questionText = Array.isArray(data)
        ? data.find(item => item.category === category)?.question || t.emotion.defaultQuestion
        : t.emotion.defaultQuestion;
    const translatedQuestion = useTranslatedText(questionText, lanText);

    // ── reputationData title lookup ──────────────────────────────────
    const reputationData = [
        { key: "veryhigh", title: t?.reputation?.veryhigh || "Very High", desc: "Top tier reputation!", color: "#EB7C69" },
        { key: "high", title: t?.reputation?.high || "High", desc: "Strong reputation!", color: "#DA737E" },
        { key: "good", title: t?.reputation?.good || "Good", desc: "Well respected!", color: "#C1659C" },
        { key: "average", title: t?.reputation?.average || "Average", desc: "Normal reputation!", color: "#9A50CA" },
        { key: "low", title: t?.reputation?.low || "Low", desc: "Needs improvement!", color: "#773EF4" },
    ];

    const emojiData = [
        { key: "veryhigh", emoji: veryhighLottie, bg: "#EB7C69" },
        { key: "high", emoji: highLottie, bg: "#DA737E" },
        { key: "good", emoji: goodLottie, bg: "#C1659C" },
        { key: "average", emoji: averageLottie, bg: "#9A50CA" },
        { key: "low", emoji: lowLottie, bg: "#773EF4" },
    ];

    const checkInternetConnection = async () => {
        await new Promise(resolve => setTimeout(resolve, 200));
        if (!navigator.onLine) return false;
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);
            await fetch('https://www.google.com/favicon.ico', { mode: 'no-cors', signal: controller.signal });
            clearTimeout(timeoutId);
            return true;
        } catch (error) {
            return false;
        }
    };

    // ── Audio init ───────────────────────────────────────────────
    useEffect(() => {
        spinWinAudioRef.current = new Audio(spinWin);
        spinWinAudioRef.current.preload = "auto";
        spinWinAudioRef.current.loop = false;
        spinWinAudioRef.current.load();
    }, []);

    // ── Image preload ────────────────────────────────────────────
    useEffect(() => {
        const imagesToPreload = [reputationbutton, star];
        let loadedCount = 0;
        let hasError = false;
        const totalImages = imagesToPreload.length;

        const timeoutId = setTimeout(() => {
            if (!imagesLoaded && !hasError) setImageLoadError(true);
        }, 5000);

        const checkAllLoaded = () => {
            loadedCount++;
            if (loadedCount === totalImages && starImgLoaded) {
                clearTimeout(timeoutId);
                setTimeout(() => { setImagesLoaded(true); }, 500);
            }
        };

        const handleError = () => {
            hasError = true;
            clearTimeout(timeoutId);
            setImageLoadError(true);
        };

        imagesToPreload.forEach((src) => {
            const img = new Image();
            img.onload = checkAllLoaded;
            img.onerror = handleError;
            img.src = src;
        });

        return () => clearTimeout(timeoutId);
    }, [lanText, starImgLoaded]);

    useEffect(() => {
        let isMounted = true;

        const noNetDarkCached = localStorage.getItem('noNetImageDark');
        const noUserDarkCached = localStorage.getItem('noUserImageDark');

        if (isMounted) {
            setNoNetImgLoaded(true);
            setNoNetDarkImgSrc(noNetDarkCached || NoNetDark);
            setNoUserImgLoaded(true);
            setNoUserDarkImgSrc(noUserDarkCached || NouserDark);
        }

        storeImageInLocalStorage('starImage', star)
            .then((dataURL) => {
                if (isMounted) { setStarImgSrc(dataURL); setStarImgLoaded(true); }
            })
            .catch(() => {
                if (isMounted) { setStarImgSrc(star); setStarImgLoaded(true); }
            });

        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        const checkInternet = async () => {
            const isOnline = await checkInternetConnection();
            if (!isOnline) { setNoInternet(true); }
        };
        checkInternet();
    }, []);

    // ── Cleanup interval + navigation timer on unmount ───────────
    useEffect(() => {
        return () => {
            if (pressIntervalRef.current) clearInterval(pressIntervalRef.current);
            if (navigationTimerRef.current) clearTimeout(navigationTimerRef.current);
        };
    }, []);

    // ── Stop auto-navigation on tab hide ─────────────────────────
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Stop the fill interval if user is holding when tab hides
                if (pressIntervalRef.current) {
                    clearInterval(pressIntervalRef.current);
                    pressIntervalRef.current = null;
                }
                isPressedRef.current = false;

                // Cancel pending navigation
                if (navigationTimerRef.current) {
                    clearTimeout(navigationTimerRef.current);
                    navigationTimerRef.current = null;
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, []);

    // ── Stop auto-navigation on back button ──────────────────────
    useEffect(() => {
        const handlePopState = () => {
            if (pressIntervalRef.current) {
                clearInterval(pressIntervalRef.current);
                pressIntervalRef.current = null;
            }
            isPressedRef.current = false;

            if (navigationTimerRef.current) {
                clearTimeout(navigationTimerRef.current);
                navigationTimerRef.current = null;
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const navigateToNextScreen = useCallback(async (lottieKey) => {
        const resolvedKey = lottieKey || activeLottie;

        const formData = {
            originalData,
            hint,
            uploadedImage,
            translatedQuestion,
            reputationKey: resolvedKey
        };

        sessionStorage.setItem('reputationFlowData', JSON.stringify(formData));

        navigate(`/${username || ''}/${category || ''}/reputationcard`, {
            state: formData,
            replace: false
        });
    }, [activeLottie, originalData, hint, uploadedImage, translatedQuestion, username, category, navigate]);

    // ── Press start: begin incrementing fillPoints ───────────────
    const handlePressStart = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!navigator.onLine) {
            showSnackbar(t?.common?.nointertoast || "Please turn on internet connection!", "error");
            return;
        }

        if (isPressedRef.current) return;
        isPressedRef.current = true;

        // If already at max, reset to 0 before starting again
        if (fillPointsRef.current >= TOTAL_POINTS) {
            fillPointsRef.current = 0;
            setFillPoints(0);
            setActiveLottie(null);
        }

        pressIntervalRef.current = setInterval(() => {
            if (fillPointsRef.current >= TOTAL_POINTS) {
                clearInterval(pressIntervalRef.current);
                pressIntervalRef.current = null;

                // ── Auto-release: trigger handlePressEnd logic when 100 is hit ──
                isPressedRef.current = false;

                const lottieKey = getLottieForPoints(TOTAL_POINTS);
                setActiveLottie(lottieKey);

                // Play spinWin audio
                try {
                    if (navigator.onLine && spinWinAudioRef.current) {
                        spinWinAudioRef.current.currentTime = 0;
                        spinWinAudioRef.current.play();
                    }
                } catch (err) {
                    console.warn("Win audio error:", err);
                }

                if (!isIOS) {
                    navigationTimerRef.current = setTimeout(() => {
                        navigateToNextScreen(lottieKey);
                    }, 5000);
                }
                return;
            }
            fillPointsRef.current += 1;
            setFillPoints(fillPointsRef.current);
        }, 100);
    }, [showSnackbar, t, isIOS, navigateToNextScreen]);

    // ── Press end: stop incrementing, lock in the lottie ─────────
    const handlePressEnd = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!isPressedRef.current) return;
        isPressedRef.current = false;

        if (pressIntervalRef.current) {
            clearInterval(pressIntervalRef.current);
            pressIntervalRef.current = null;
        }

        const pts = fillPointsRef.current;
        if (pts > 0 && pts < TOTAL_POINTS) {
            // Normal partial-press release
            const lottieKey = getLottieForPoints(pts);
            setActiveLottie(lottieKey);

            // Play spinWin audio on modal open
            try {
                if (navigator.onLine && spinWinAudioRef.current) {
                    spinWinAudioRef.current.currentTime = 0;
                    spinWinAudioRef.current.play();
                }
            } catch (err) {
                console.warn("Win audio error:", err);
            }

            if (!isIOS) {
                navigationTimerRef.current = setTimeout(() => {
                    navigateToNextScreen(lottieKey);
                }, 5000);
            }
        }
        // pts === TOTAL_POINTS case is already handled inside the interval above
    }, [isIOS, navigateToNextScreen]);

    // ── Derived fill fractions ───────────────────────────────────
    const fillFractions = getFillFractions(fillPoints);

    const fillByKey = {};
    TIERS.forEach((tier, i) => {
        fillByKey[tier.key] = { ...fillFractions[i], color: tier.color };
    });

    const getTopPosition = () => {
        switch (activeLottie) {
            case "low": return "-20dvh";
            case "average": return "-10dvh";
            case "good": return "5dvh";
            case "high": return "15dvh";
            case "veryhigh": return "25dvh";
            default: return "50%";
        }
    };

    const getTopPositionLeft = () => {
        switch (activeLottie) {
            case "low":
            case "average":
            case "good":
            case "high":
            case "veryhigh":
                return window.innerWidth < 500 ? "30vw" : "200px";
            default:
                return "50%";
        }
    };

    useEffect(() => {
        preloadImages.forEach((src) => {
            const img = new Image();
            img.src = src;
        });
    }, []);

    const currentNoNetImg = noNetDarkImgSrc;
    const currentNoUserImg = noUserDarkImgSrc;

    if (noInternet) {
        return (
            <NoInternet
                style={{ height: '100vh' }}
                t={t?.noData || {}}
                Nouser={currentNoNetImg}
                imgLoaded={noNetImgLoaded}
            />
        );
    }

    if (imageLoadError) {
        return (
            <NoDataFound2
                style={{ height: '100vh' }}
                t={t?.noData2 || {}}
                Nouser={currentNoUserImg}
                username={username}
                category={category}
                imgLoaded={noUserImgLoaded}
            />
        );
    }

    if (!imagesLoaded) {
        return (
            <div className='GTRegular' style={{ background: "#1C1C1C", height: '100dvh' }}>
                <div className='h-100'>
                    <Row className={`h-100`}>
                        <Col className="p-0 d-flex flex-column align-items-between justify-content-between" style={{ height: '100%' }}>
                            <div className="d-flex justify-content-center align-items-center" style={{ height: '11%', padding: '20px 0' }}>
                                <div className="shimmer2" style={{ width: "85%", height: "25px", maxWidth: "90vw", borderRadius: "12px" }}></div>
                            </div>
                            <div className="d-flex justify-content-center align-items-center mt-1" style={{ minHeight: isSmallScreen ? "270px" : "49vh" }}>
                                <div className="d-flex flex-column justify-content-center align-items-center h-100 rounded-19 shimmer-element2 position-relative"
                                    style={{ width: "450px", maxWidth: isSmallScreen ? "81vw" : "87vw", padding: "20px" }}>
                                    <div className="shimmer-light2 position-absolute" style={{ width: "75%", height: "50px", top: "-25px", borderRadius: "8px" }}></div>
                                </div>
                            </div>
                            <div className="d-flex flex-column justify-content-center align-items-center px-3 mb-3" style={{ height: '300px', gap: "15px" }}>
                                <div className="shimmer-light2" style={{ width: "60%", height: "40px", borderRadius: "12px", maxWidth: "300px" }}></div>
                                <div className="shimmer-element2" style={{ width: "clamp(90px, 10vw, 110px)", height: "clamp(90px, 10vw, 110px)", borderRadius: "50%" }}></div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>
        );
    }

    return (
        <div className='GTRegular' style={{ height: '100dvh', background: "linear-gradient(145deg, #703AFC, #EB7C69)" }}>
            <div className='h-100 pt-42'>
                <Row className={`h-100`}>
                    <Col className="p-0 d-flex flex-column align-items-between justify-content-between" style={{ height: '100%' }}>

                        <div className="d-flex justify-content-center align-items-center" style={{ height: '12%', padding: '20px 0' }}>
                            <div className="text-center text-white" style={{ maxWidth: "90vw" }}>
                                <p className="lh-sm mx-auto px-3 m-0 mb-3 mt-1"
                                    style={{ fontSize: "17px", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                    {t.emotion.text} <br /> {t.emotion.text2}
                                </p>
                            </div>
                        </div>

                        <div className="d-flex justify-content-center align-items-center mt-3" style={{ flex: "1 0 auto" }}>
                            <div className="d-flex justify-content-center align-items-center h-100 border border-1 border-black position-relative"
                                style={{ width: "450px", maxWidth: isSmallScreen ? "88vw" : "88vw", background: "#100C34", borderRadius: "28px" }}>

                                <div className="position-relative d-flex pt-2 gap-3  align-items-end" style={{ width: "80%", height: "90%" }}>

                                    {/* Left Column – circles + lines with fill animation */}
                                    <div className="pb-2" style={{ width: "80px" , height: "100%"}}>
                                        <div className="d-flex flex-column h-100">
                                            {emojiData.map((item, index) => {
                                                const fill = fillByKey[item.key] || { circleFill: 0, lineFill: 0, color: item.bg };
                                                const circleBorder = fill.circleFill > 0 ? "2px solid #34314E" : "2px solid #34314E";
                                                const lineFill = fill.lineFill;
                                                const lineBaseColor = "#34314E";
                                                const lineFilledColor = fill.color;

                                                return (
                                                    <div key={index} className="d-flex flex-column align-items-center position-relative" style={{ flex: 1 }}>
                                                        {/* Top line segment */}
                                                        <div style={{
                                                            flex: "1", width: "2px",
                                                            visibility: index === 0 ? "hidden" : "visible",
                                                            position: "relative", overflow: "hidden",
                                                            background: lineBaseColor,
                                                        }}>
                                                            <div style={{
                                                                position: "absolute", bottom: 0, left: 0,
                                                                width: "100%", height: `${lineFill * 100}%`,
                                                                background: lineFilledColor, borderRadius: "2px",
                                                            }} />
                                                        </div>

                                                        {/* Circle */}
                                                        <div className="position-relative" style={{ display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
                                                            <div className="d-flex justify-content-center align-items-center rounded-circle"
                                                                style={{
                                                                    width: isSmallScreen ? "45px" : "60px", height: isSmallScreen ? "45px" : "60px",
                                                                    position: "relative", overflow: "hidden",
                                                                    background: "transparent", border: circleBorder,
                                                                }}>
                                                                <div style={{
                                                                    position: "absolute", bottom: 0, left: 0,
                                                                    width: "100%", height: `${fill.circleFill * 100}%`,
                                                                    background: fill.color, zIndex: 0,
                                                                }} />
                                                            </div>

                                                            {/* Small lottie inside circle (hidden when active) */}
                                                            <div style={{
                                                                position: "absolute", top: isSmallScreen ? "50%" : "50%", left: "50%",
                                                                transform: "translate(-50%, -50%)", zIndex: 6,
                                                                display: "flex", justifyContent: "center", alignItems: "center",
                                                            }}>
                                                                <Lottie
                                                                    animationData={item.emoji}
                                                                    loop
                                                                    autoplay={item.key === activeLottie}
                                                                    style={{
                                                                        width: isSmallScreen ? 30 : 40, height: isSmallScreen ? 30 : 40,
                                                                        opacity: activeLottie === item.key ? 0 : 1,
                                                                        transition: "opacity 0.1s ease 0s"
                                                                    }}
                                                                />
                                                            </div>

                                                            {/* Large lottie that animates to center */}
                                                            <div style={{
                                                                position: "absolute",
                                                                top: getTopPosition(),
                                                                left: getTopPositionLeft(),
                                                                transition: "transform 0.2s ease 0s, width 0.5s ease 1s, top 1s ease 1s, left 1s ease 1s",
                                                                transform: activeLottie === item.key ? "translate(-50%, -50%) scale(1)" : "translate(-50%, -50%) scale(0)",
                                                                zIndex: 8,
                                                                display: "flex", justifyContent: "center", alignItems: "center",
                                                            }}>
                                                                <Lottie
                                                                    animationData={item.emoji}
                                                                    loop={true} autoplay={true}
                                                                    style={{
                                                                        width: activeLottie === item.key ? 180 : isSmallScreen ? 30 : 40,
                                                                        height: activeLottie === item.key ? 180 : isSmallScreen ? 30 : 40,
                                                                        transition: "width 0.5s ease 1s",
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Right Column */}
                                    <div className="flex-grow-1 d-flex flex-column" style={{ height : "98%"}}>
                                        {reputationData.map((item, index) => (
                                            <div key={index} className="flex-grow-1 d-flex flex-column justify-content-center"
                                                style={{ borderTop: index !== 0 ? "1px solid #312450" : "none" }}>
                                                <div className="GTMedium" style={{ color: item.color, fontSize: "clamp(15px, 2vw, 18px)" }}>
                                                    {item.title}
                                                </div>
                                                <div style={{ color: "#878787", fontSize: "12px" }}>
                                                    {item.desc}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Question pill */}
                                <div className="position-absolute rounded-pill"
                                    style={{ top: "-30px", width: "80%", padding: "2px", background: "linear-gradient(to bottom, #EB7C69, #7F50FA)" }}>
                                    <div
                                        className="rounded-pill text-center text-white border border-1 border-black d-flex align-items-center justify-content-center px-3 GTBold"
                                        style={{
                                            minHeight: "43px",
                                            background: "#100C34",
                                            fontWeight: "900",
                                            fontSize:
                                                translatedQuestion.length > 100
                                                    ? "10px"
                                                    : translatedQuestion.length > 80
                                                        ? "11px"
                                                        : translatedQuestion.length > 60
                                                            ? "12px"
                                                            : translatedQuestion.length > 40
                                                                ? "14px"
                                                                : "16px",
                                        }}
                                    >
                                        <p
                                            className="m-0"
                                            style={{
                                                lineHeight: "17px",
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {t?.common?.que || "Que:"} {translatedQuestion}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex justify-content-center align-items-center px-3" style={{ height: '20dvh' }}>
                            <div className="position-relative my-3 d-flex flex-column justify-content-center align-items-center"
                                style={{ width: "450px", maxWidth: isSmallScreen ? "88vw" : "88vw", height: "80%", background: "#442144", borderRadius: "23px" }}>
                                <div
                                    className={`press-image-btn mx-auto`}
                                    onMouseDown={handlePressStart}
                                    onMouseUp={handlePressEnd}
                                    onMouseLeave={handlePressEnd}
                                    onTouchStart={(e) => { e.preventDefault(); handlePressStart(e); }}
                                    onTouchEnd={(e) => { e.preventDefault(); handlePressEnd(e); }}
                                    onTouchCancel={(e) => { e.preventDefault(); handlePressEnd(e); }}
                                    style={{
                                        width: "clamp(110px, 10vw, 130px)", height: "clamp(110px, 10vw, 130px)", flex: "0 0 90%",
                                        backgroundImage: `url(${reputationbutton})`,
                                        backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center",
                                        cursor: !!activeLottie ? "not-allowed" : "pointer",
                                        userSelect: "none",
                                        opacity: !!activeLottie ? 0.5 : 1,
                                        transition: "all 0.2s",
                                        WebkitTouchCallout: "none", WebkitUserSelect: "none",
                                        WebkitTapHighlightColor: "transparent", touchAction: "none",
                                        pointerEvents: !!activeLottie ? "none" : "auto"
                                    }}
                                >
                                    <img src={tap} alt=""
                                        style={{
                                            position: "absolute", top: "35%", left: "50%",
                                            transform: "translate(-50%, -50%)",
                                            width: "40%", height: "40%", objectFit: "contain", pointerEvents: "none",
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                    </Col>
                </Row>
            </div>

            <RotatePhonePopup excludeWhen={[]} />

            <AnimatePresence>
                {activeLottie && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 1, delay: 1 }}
                        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{ zIndex: 7, backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(5px)', overflow: 'hidden' }}
                    >
                        {[
                            { width: 35, style: { top: '50%', left: '15%' }, animate: { x: [0, 15, -10, 8, -5, 0], y: [0, -8, 6, -4, 3, 0], rotate: [0, 10, -10, 5, -5, 0] }, duration: 5, delay: 0 },
                            { width: 20, style: { top: '55%', right: '10%' }, animate: { x: [0, -12, 8, -6, 4, 0], y: [0, 10, -7, 5, -3, 0], rotate: [0, -15, 15, -8, 8, 0] }, duration: 6, delay: 0.5 },
                            { width: 6, style: { bottom: '65%', left: '12%' }, animate: { x: [0, 10, -8, 6, -4, 0], y: [0, -12, 9, -6, 4, 0], rotate: [0, 20, -20, 10, -10, 0] }, duration: 7, delay: 1 },
                            { width: 35, style: { top: '30%', right: '35%' }, animate: { x: [0, 15, -10, 8, -5, 0], y: [0, -8, 6, -4, 3, 0], rotate: [0, 10, -10, 5, -5, 0] }, duration: 5, delay: 0 },
                            { width: 10, style: { top: '35%', right: '10%' }, animate: { x: [0, -12, 8, -6, 4, 0], y: [0, 10, -7, 5, -3, 0], rotate: [0, -15, 15, -8, 8, 0] }, duration: 6, delay: 0.5 },
                            { width: 6, style: { top: '25%', right: '12%' }, animate: { x: [0, 10, -8, 6, -4, 0], y: [0, -12, 9, -6, 4, 0], rotate: [0, 20, -20, 10, -10, 0] }, duration: 7, delay: 1 },
                            { width: 10, style: { bottom: '35%', right: '15%' }, animate: { x: [0, -15, 10, -8, 5, 0], y: [0, -10, 8, -5, 3, 0], rotate: [0, -12, 12, -6, 6, 0] }, duration: 5.5, delay: 0.3 },
                            { width: 7, style: { top: '41%', left: '50%', transform: 'translateX(-50%)' }, animate: { x: [0, 8, -6, 5, -3, 0], y: [0, 12, -8, 6, -4, 0], rotate: [0, 15, -15, 8, -8, 0] }, duration: 6.5, delay: 1.5 },
                            { width: 5, style: { top: '45%', left: '8%' }, animate: { x: [0, 12, -9, 7, -5, 0], y: [0, -8, 6, -4, 3, 0], rotate: [0, -18, 18, -9, 9, 0] }, duration: 7.5, delay: 0.8 },
                            { width: 9, style: { top: '50%', right: '8%' }, animate: { x: [0, -10, 8, -6, 4, 0], y: [0, 10, -7, 5, -3, 0], rotate: [0, 12, -12, 6, -6, 0] }, duration: 5.8, delay: 1.2 },
                            { width: 12, style: { bottom: '30%', left: '50%', transform: 'translateX(-50%)' }, animate: { x: [0, -8, 6, -5, 3, 0], y: [0, -10, 7, -5, 3, 0], rotate: [0, 25, -25, 12, -12, 0] }, duration: 6.8, delay: 0.6 },
                        ].map((s, i) => (
                            <motion.img key={i} src={starImgSrc || star} alt="star" width={s.width}
                                className="position-absolute" style={s.style}
                                animate={s.animate}
                                transition={{ duration: s.duration, repeat: Infinity, repeatType: "loop", ease: "easeInOut", delay: s.delay }}
                            />
                        ))}

                        {isIOS && (
                            <Button
                                onClick={() => navigateToNextScreen(activeLottie)}
                                className="rounded-pill border-0 mx-auto GTMedium text-white position-absolute"
                                style={{
                                    padding: "10px 1px", height: "50px", width: "min(350px, 80vw)", maxWidth: "350px",
                                    bottom: '20px', left: '50%', transform: 'translateX(-50%)',
                                    background: getLottieColor(activeLottie),
                                    cursor: 'pointer', touchAction: 'none', WebkitTapHighlightColor: 'transparent',
                                }}
                            >
                                <span className='GTMedium' style={{ fontSize: "clamp(21px, 4.5vw, 27px)", lineHeight: 1, whiteSpace: "nowrap" }}>
                                    {t?.common?.next || 'Next'}
                                </span>
                            </Button>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

        </div>
    );
};

export default Category12;